import React, { useState } from 'react';
import {
  Box,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Badge,
  Button,
  Text,
  Heading,
  RefreshIcon,
  InfoIcon,
} from '@razorpay/blade/components';
import { usePagination } from 'hooks/usePagination';
import { useMissingInformationReport } from './hooks/useMissingInformationReport';
import { getTableToolbarText } from './utils';
import { ErrorView } from 'components/ui/ErrorView/ErrorView';
import { MissingNameMismatchBankAccount } from 'api/types/missingInformationReportTypes';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { BladeTooltipWrapper } from 'components/ui/BladeTooltipWrapper/BladeTooltipWrapper';
import { TableToolbar } from '@razorpay/blade/components';
import { NameMismatchDetailsDrawer } from './components/NameMismatchDetailsDrawer';

export const NameMismatchBankAccount = () => {
  const [selectedAccount, setSelectedAccount] = useState<MissingNameMismatchBankAccount | null>(
    null,
  );
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  const {
    data: employeeData,
    isError,
    isLoading,
    isFetching,
    refetch,
  } = useMissingInformationReport(
    'name_mismatch_bank_account_details',
    currentPage,
    itemsPerPage,
  );

  const shouldShowEmptyView =
    !isError && !isLoading && !isFetching && employeeData?.data.items.length === 0;
  const shouldShowErrorView = isError;
  const shouldShowTable = !shouldShowEmptyView && !shouldShowErrorView;

  return (
    <Box>
      <Heading
        size="large"
        weight="semibold"
        color="surface.text.gray.normal"
        marginBottom="spacing.2">
        Bank Account Name Mismatch
      </Heading>
      <Text color="surface.text.gray.subtle" marginBottom="spacing.7">
        Review and resolve employees whose bank account holder name does not match their employee
        name
      </Text>

      {isError && <ErrorView onRefetch={refetch} />}
      {shouldShowEmptyView && (
        <EmptyListView
          title="🔍 No Missing Information Found"
          description="Great! All employees have completed their details."
        />
      )}
      {shouldShowTable && (
        <Table
          isLoading={isLoading}
          isRefreshing={isFetching}
          data={{
            nodes: employeeData?.data.items || [],
          }}
          backgroundColor="surface.background.gray.intense"
          toolbar={
            <TableToolbar
              title={getTableToolbarText(
                employeeData?.data.total_count || 0,
                currentPage,
                itemsPerPage,
              )}>
              <Box display="flex" justifyContent="flex-end" gap="spacing.5">
                <Button
                  icon={RefreshIcon}
                  variant="tertiary"
                  accessibilityLabel="Refresh Bank Account Name Mismatch"
                  onClick={refetch}
                  isDisabled={isFetching || isLoading}
                />
              </Box>
            </TableToolbar>
          }
          pagination={
            <TablePagination
              paginationType="server"
              showPageSizePicker={true}
              currentPage={currentPage}
              totalItemCount={employeeData?.data.total_count || 0}
              onPageChange={({ page }) => setCurrentPage(page)}
              onPageSizeChange={({ pageSize }) => {
                setItemsPerPage(pageSize);
                setCurrentPage(0);
              }}
            />
          }>
          {(tableData) => (
            <>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>
                    <Box display="flex" alignItems="center" gap="spacing.2">
                      <Text>Account Holder Name</Text>
                      <BladeTooltipWrapper content="fetched from bank via penny test" placement="top">
                        <InfoIcon size="small" color="surface.icon.gray.muted" />
                      </BladeTooltipWrapper>
                    </Box>
                  </TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {tableData.map((employee) => (
                  <TableRow
                    key={employee.id}
                    item={employee}
                    hoverActions={
                      <Box display="flex" gap="spacing.4">
                        <Button
                          size="xsmall"
                          variant="secondary"
                          onClick={() => setSelectedAccount(employee)}>
                          View Details
                        </Button>
                      </Box>
                    }>
                    <TableCell>
                      <Box>
                        <Text color="surface.text.gray.subtle">{employee.name}</Text>
                        <Text size="small" color="surface.text.gray.muted">
                          {employee.employee_id}
                        </Text>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Text color="surface.text.gray.subtle">{employee.account_holder_name}</Text>
                    </TableCell>
                    <TableCell>
                      <Badge color="negative" emphasis="subtle">
                        Name Mismatch
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          )}
        </Table>
      )}
      <NameMismatchDetailsDrawer
        selectedAccount={selectedAccount}
        onClose={() => setSelectedAccount(null)}
      />
    </Box>
  );
};
